import React from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { css } from '@emotion/css';
import {
  Box,
  Chip,
  Paper,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';
import { BiCalendar, BiPhone } from 'react-icons/bi';
import { MdDirectionsCar } from 'react-icons/md';
import { GetMobileServiceAppointmentsQuery } from '../../mobileService/queries/getMobileServiceAppointments.query.generated';

import { formatAppointmentType } from '../utils/appointmentType';

type Appointment = GetMobileServiceAppointmentsQuery['appointments'][number];

const STATUS_CHIPS: Record<string, { label: string; color: 'default' | 'success' | 'warning' | 'info' | 'error' }> = {
  NEW: { label: 'New', color: 'info' },
  PENDING: { label: 'Pending', color: 'warning' },
  READY: { label: 'Ready', color: 'success' },
  PAYING: { label: 'Paying', color: 'info' },
  PAID: { label: 'Paid', color: 'success' },
  FAILED: { label: 'Failed', color: 'error' },
  CANCELED: { label: 'Canceled', color: 'error' },
  REFUNDED: { label: 'Refunded', color: 'default' },
};

interface AppointmentCardProps {
  appointment: Appointment;
}

const AppointmentCard: React.FC<AppointmentCardProps> = ({ appointment }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const cls = useStyles(theme);

  const statusCfg = STATUS_CHIPS[appointment.status] || { label: appointment.status, color: 'default' as const };
  const vehicle = [appointment.vehicle_year, appointment.vehicle_make, appointment.vehicle_model]
    .filter(Boolean)
    .join(' ');
  const formattedTime = appointment.appointment_time
    ? dayjs(appointment.appointment_time).format('MMM D, YYYY · h:mm A')
    : 'No time set';

  return (
    <Paper className={cls.card} elevation={1} onClick={() => navigate(`/appointments/${appointment.id}`)}>
      {/* Header row */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5, gap: 1 }}>
            <Typography variant='body2' sx={{ fontWeight: 600, fontSize: 15 }} noWrap>
              {appointment.consumer_name || 'Unknown'}
            </Typography>
            <Chip
              label={statusCfg.label}
              size='small'
              color={statusCfg.color}
              variant='outlined'
              sx={{ fontWeight: 600, fontSize: 11, height: 22 }}
            />
          </Box>

          {/* ID + type */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant='caption' color='text.secondary'>
              #{appointment.id}
            </Typography>
            <Chip
              label={formatAppointmentType(appointment.type)}
              size='small'
              variant='outlined'
              sx={{ fontSize: 10, height: 18 }}
            />
          </Box>
        </Box>
      </Box>

      {/* Info rows */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mt: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BiCalendar size={14} color={theme.palette.text.secondary} />
          <Typography variant='body2' color='text.secondary' sx={{ fontSize: 13 }}>
            {formattedTime}
          </Typography>
        </Box>

        {appointment.consumer_phone ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BiPhone size={14} color={theme.palette.text.secondary} />
            <Typography variant='body2' color='text.secondary' sx={{ fontSize: 13 }}>
              {appointment.consumer_phone}
            </Typography>
          </Box>
        ) : null}

        {vehicle ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MdDirectionsCar size={14} color={theme.palette.text.secondary} />
            <Typography variant='body2' color='text.secondary' sx={{ fontSize: 13 }}>
              {vehicle}
              {appointment.vehicle_color ? ` · ${appointment.vehicle_color}` : ''}
            </Typography>
          </Box>
        ) : null}

        {appointment.vehicle_vin ? (
          <Typography variant='caption' color='text.disabled' sx={{ pl: 3, fontSize: 11, letterSpacing: 0.5 }}>
            VIN: {appointment.vehicle_vin}
          </Typography>
        ) : null}

        {appointment.driver_notes ? (
          <Box sx={{ mt: 0.5 }}>
            <Typography
              variant='body2'
              color='text.secondary'
              sx={{
                fontSize: 12,
                fontStyle: 'italic',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {appointment.driver_notes}
            </Typography>
          </Box>
        ) : null}
      </Box>
    </Paper>
  );
};

const useStyles = (theme: Theme) => ({
  card: css`
    padding: 16px;
    cursor: pointer;
    border: 2px solid transparent;
    transition: all 0.15s ease-in-out;
    &:hover {
      transform: translateY(-1px);
      box-shadow: ${theme.shadows[4]};
      border-color: ${theme.palette.divider};
    }
  `,
});

export default AppointmentCard;
